/**
 * Staff ID card — shareable pass with an arch-shaped photo frame (the
 * design's signature avatar motif), verified badge, flats served, faux QR,
 * gate code + attendance summary link. Wired: GET /api/services/staff/:id
 * (the detail endpoint — it returns flats served; the list does not).
 * Port of screens-services.jsx StaffIdCard.
 */
import * as ImagePicker from 'expo-image-picker';
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Image, Linking, Pressable, Text as RNText, View } from 'react-native';

import { formatINR, palette, radius, space } from '@bero/tokens';
import { bool, num, str } from '@/components/gate/api';
import { QRBox } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { roleLabel } from './index';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Field, Hairline, IconButton, Input, ListRow, Screen, Sheet, Skeleton, TopBar } from '@/ui';

/** Fixed brand-green chrome — matches the gate-pass ticket band. */
const BRAND = palette.light;
const BRAND_FIGURE = palette.dark.accent500;

function IdMark({ height = 17 }: { height?: number }) {
  const u = height / 58;
  return (
    <View style={{ width: 50 * u, height }}>
      <View style={{
        position: 'absolute', left: 2 * u, top: 1 * u, width: 46 * u, height: 55 * u,
        borderWidth: 9 * u, borderBottomWidth: 0, borderColor: BRAND.canvas0,
        borderTopLeftRadius: 23 * u, borderTopRightRadius: 23 * u,
      }} />
      <View style={{ position: 'absolute', left: 19 * u, top: 24 * u, width: 12 * u, height: 12 * u, borderRadius: 6 * u, backgroundColor: BRAND_FIGURE }} />
      <View style={{ position: 'absolute', left: 20 * u, top: 40 * u, width: 10 * u, height: 16 * u, borderRadius: 5 * u, backgroundColor: BRAND_FIGURE }} />
    </View>
  );
}

/** Arch-shaped photo frame — real photo when present, else the one amber
 * (accent) surface on this screen with initials. */
function ArchPhoto({ name, photo, size = 64 }: { name: string; photo?: string; size?: number }) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const shape = {
    width: size, height: size * 1.16,
    borderTopLeftRadius: size / 2, borderTopRightRadius: size / 2,
    borderBottomLeftRadius: size * 0.12, borderBottomRightRadius: size * 0.12,
    overflow: 'hidden' as const,
  };
  if (photo) {
    return <Image source={{ uri: photo }} style={[shape, { backgroundColor: colors.canvas2 }]} resizeMode="cover" />;
  }
  return (
    <View style={[shape, { backgroundColor: colors.accent100, alignItems: 'center', justifyContent: 'center' }]}>
      <AppText weight="700" style={{ color: colors.accent700, fontSize: size * 0.3 }}>{initials}</AppText>
    </View>
  );
}

/** Label stays put; value fills the rest, right-aligned, wraps cleanly. */
function FactRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s3 }}>
      <AppText variant="bodySm" tone="tertiary" style={{ flexShrink: 0, paddingTop: 2 }}>{label}</AppText>
      <AppText variant="bodyMd" weight="500" mono={mono} style={{ flex: 1, textAlign: 'right' }}>{value}</AppText>
    </View>
  );
}

export default function StaffIdCardScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { id } = useLocalSearchParams<{ id: string }>();
  const [member, setMember] = React.useState<unknown>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');

  React.useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}`);
        const raw = (body && typeof body === 'object' && 'staff' in body)
          ? (body as { staff: unknown }).staff : body;
        if (!cancelled) { setMember(raw); setState('ready'); }
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const [monthDays, setMonthDays] = React.useState<number | null>(null);
  const [busy, setBusy] = React.useState<'photo' | 'remove' | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  // attendance ratio: punches this month (parallel to the member fetch)
  React.useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}/attendance`);
        const items = (body && typeof body === 'object' && 'items' in body)
          ? (body as { items: unknown[] }).items : [];
        if (!cancelled) setMonthDays(Array.isArray(items) ? items.length : 0);
      } catch {
        if (!cancelled) setMonthDays(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const name = str(member, 'name', 'fullName') ?? 'Staff member';
  const role = str(member, 'role') ?? 'Help';
  const code = str(member, 'code', 'gateCode') ?? '——';
  const phone = str(member, 'phone');
  const photo = str(member, 'photo');

  const callStaff = () => {
    if (phone) void Linking.openURL(`tel:${phone}`);
  };

  const pickPhoto = async () => {
    if (busy) return;
    setBusy('photo');
    setNotice(null);
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'], base64: true, quality: 0.5, exif: false,
      });
      if (!result.canceled && result.assets?.[0]?.base64) {
        const asset = result.assets[0];
        const mime = asset.mimeType?.startsWith('image/') ? asset.mimeType : 'image/jpeg';
        const uri = `data:${mime};base64,${asset.base64}`;
        const body = await api.post<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}`, { photo: uri });
        const raw = (body && typeof body === 'object' && 'staff' in body) ? (body as { staff: unknown }).staff : body;
        setMember(raw);
      }
    } catch {
      setNotice(t('sid_photo_error'));
    } finally {
      setBusy(null);
    }
  };

  // ratings
  const avgRating = num(member, 'avgRating');
  const ratingCount = num(member, 'ratingCount') ?? 0;
  const myRating = num(member, 'myRating');
  const [rateOpen, setRateOpen] = React.useState(false);
  const [stars, setStars] = React.useState(0);
  const [rateNote, setRateNote] = React.useState('');
  const [savingRating, setSavingRating] = React.useState(false);

  const openRate = () => {
    setStars(myRating ?? 0);
    setRateNote('');
    setRateOpen(true);
  };

  const saveRating = async () => {
    if (!stars || savingRating) return;
    setSavingRating(true);
    try {
      await api.post<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}/rating`, { stars, note: rateNote.trim() });
      const body = await api.get<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}`);
      setMember((body && typeof body === 'object' && 'staff' in body) ? (body as { staff: unknown }).staff : body);
      setRateOpen(false);
    } catch {
      setNotice(t('sid_rating_error'));
      setRateOpen(false);
    } finally {
      setSavingRating(false);
    }
  };

  // payments
  const [payOpen, setPayOpen] = React.useState(false);
  const [payAmount, setPayAmount] = React.useState('');
  const [payNote, setPayNote] = React.useState('');
  const [savingPay, setSavingPay] = React.useState(false);
  const [payments, setPayments] = React.useState<{ id: string; paidOn: string; paise: number; note: string }[]>([]);

  const loadPayments = React.useCallback(async () => {
    try {
      const body = await api.get<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}/payments`);
      const items = (body && typeof body === 'object' && 'items' in body) ? (body as { items: unknown[] }).items : [];
      setPayments((Array.isArray(items) ? items : []).map((p, i) => ({
        id: str(p, 'id') ?? `p${i}`,
        paidOn: str(p, 'paidOn') ?? '',
        paise: num(p, 'amountPaise') ?? 0,
        note: str(p, 'note') ?? '',
      })));
    } catch { /* list stays empty */ }
  }, [id]);

  const openPay = () => {
    setPayAmount('');
    setPayNote('');
    setPayOpen(true);
    void loadPayments();
  };

  const deletePayment = async (pid: string) => {
    try {
      await api.post<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}/payments/${encodeURIComponent(pid)}/delete`, {});
      setPayments((prev) => prev.filter((pmt) => pmt.id !== pid));
    } catch {
      setNotice(t('sid_entry_remove_error'));
      setPayOpen(false);
    }
  };

  const savePayment = async () => {
    const rupees = Number(payAmount.replace(/[^0-9.]/g, ''));
    if (!rupees || rupees <= 0 || savingPay) return;
    setSavingPay(true);
    try {
      await api.post<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}/payments`, {
        amountPaise: Math.round(rupees * 100),
        note: payNote.trim(),
      });
      setPayAmount('');
      setPayNote('');
      await loadPayments();
    } catch {
      setNotice(t('sid_pay_error'));
      setPayOpen(false);
    } finally {
      setSavingPay(false);
    }
  };

  const removeFromFlat = async () => {
    if (busy) return;
    setBusy('remove');
    setNotice(null);
    try {
      await api.post<unknown>(`/api/services/staff/${encodeURIComponent(String(id))}/flats`, { action: 'remove' });
      router.back();
    } catch {
      setNotice(t('sid_remove_error'));
      setBusy(null);
    }
  };
  const flatList = (member as { flats?: { code?: string }[] } | null)?.flats;
  const flat = Array.isArray(flatList) && flatList.length
    ? flatList.map((f) => f.code).filter(Boolean).join(', ')
    : str(member, 'flat', 'worksFor') ?? '—';
  const days = str(member, 'days', 'schedule') ?? '—';
  const verified = bool(member, 'idVerified', 'verified') === true;

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('sid_title')}
        onBack={() => router.back()}
        right={
          <IconButton label={t('sid_more_options')} onPress={() => { /* TODO(wire): manage staff */ }}>
            <Icon name="more-horizontal" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            <Skeleton h={260} r={radius.xl} />
            <Skeleton h={44} />
          </View>
        ) : !member ? (
          <EmptyState
            icon={<Icon name="user" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('sid_error_title') : t('sid_notfound_title')}
            body={t('sid_error_body')}
          />
        ) : (
          <>
            <Card style={{ borderRadius: radius.xl, padding: 0, overflow: 'hidden' }}>
              {/* fixed brand-green band, like the gate-pass ticket header */}
              <View style={{
                backgroundColor: BRAND.primary700, paddingVertical: space.s4, paddingHorizontal: space.s5,
                flexDirection: 'row', alignItems: 'center', gap: 9,
              }}>
                <IdMark height={17} />
                <RNText style={{ fontFamily: 'Sora_800ExtraBold', fontSize: 14, letterSpacing: -0.56, color: BRAND.canvas0 }}>bero</RNText>
                <AppText variant="label" style={{ marginLeft: 'auto', color: BRAND.primary100 }}>{`${t('sid_badge')} · One Meraki`}</AppText>
              </View>

              {/* identity — arch photo frame */}
              <View style={{ padding: space.s5, flexDirection: 'row', gap: space.s4, alignItems: 'center' }}>
                <ArchPhoto name={name} photo={photo} />
                <View style={{ flex: 1 }}>
                  <AppText variant="headingMd" weight="600">{name}</AppText>
                  <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 2 }}>{roleLabel(role, t)}</AppText>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    {verified ? (
                      <>
                        <Icon name="check-circle" size={14} color={colors.success500} />
                        <AppText variant="bodySm" tone="secondary">{t('sid_id_verified')}</AppText>
                      </>
                    ) : (
                      <>
                        <Icon name="clock" size={14} color={colors.warning500} />
                        <AppText variant="bodySm" tone="secondary">{t('sid_verification_pending')}</AppText>
                      </>
                    )}
                  </View>
                </View>
              </View>

              <View style={{ marginHorizontal: space.s5 }}><Hairline /></View>

              {/* QR + facts — labels never collide with long values: label is
                  fixed, value takes the rest and wraps right-aligned. */}
              <View style={{ padding: space.s5, flexDirection: 'row', alignItems: 'center', gap: space.s4 }}>
                <QRBox size={92} seed={`STAFF-${code}`} />
                <View style={{ flex: 1, gap: space.s2 }}>
                  <FactRow label={t('sid_works_for')} value={flat} />
                  <FactRow label={t('sid_gate_code')} value={code} mono />
                  <FactRow label={t('kp_schedule')} value={days} />
                  {monthDays != null ? (
                    <FactRow label={t('sid_this_month')} value={t(monthDays === 1 ? 'sid_days_in_one' : 'sid_days_in_many', { n: monthDays })} />
                  ) : null}
                </View>
              </View>
            </Card>

            <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s4 }}>
              {phone ? (
                <Button title={t('vd_call')} kind="secondary" style={{ flex: 1 }} onPress={callStaff} />
              ) : null}
              <Button
                title={busy === 'photo' ? t('sid_updating') : photo ? t('sid_change_photo') : t('sid_add_photo')}
                kind="secondary"
                style={{ flex: 1 }}
                onPress={() => void pickPhoto()}
              />
            </View>

            <Card style={{ marginTop: space.s4, padding: 0 }}>
              <ListRow
                left={<AppText weight="700" style={{ color: colors.accent700, fontSize: 18 }}>★</AppText>}
                title={avgRating != null ? t(ratingCount === 1 ? 'sid_rating_summary_one' : 'sid_rating_summary_many', { avg: avgRating, n: ratingCount }) : t('sid_no_ratings')}
                subtitle={myRating != null ? t(myRating === 1 ? 'sid_you_rated_one' : 'sid_you_rated_many', { n: myRating }) : t('sid_rate_prompt')}
                onPress={openRate}
              />
              <Hairline inset={space.s5} />
              <ListRow
                left={<Icon name="download" size={20} color={colors.textSecondary} />}
                title={t('sid_record_payment')}
                subtitle={t('sid_record_payment_sub')}
                onPress={openPay}
              />
              <Hairline inset={space.s5} />
              <ListRow
                left={<Icon name="list" size={20} color={colors.textSecondary} />}
                title={t('sid_attendance_summary')}
                subtitle={t('sid_attendance_summary_sub')}
                onPress={() => router.push(`/attendance?staffId=${encodeURIComponent(String(id ?? ''))}&name=${encodeURIComponent(name)}`)}
              />
            </Card>

            {notice ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{notice}</AppText> : null}
            <Button
              title={busy === 'remove' ? t('sid_removing') : t('sid_remove_from_flat')}
              kind="destructive"
              style={{ marginTop: space.s4 }}
              onPress={() => void removeFromFlat()}
            />
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2, textAlign: 'center' }}>
              {t('sid_remove_note')}
            </AppText>
          </>
        )}
      </Screen>

      {/* rate sheet */}
      <Sheet visible={rateOpen} onClose={() => setRateOpen(false)}>
        <AppText variant="headingMd" weight="500">{t('sid_rate_title', { name: name.split(' ')[0] })}</AppText>
        <View style={{ flexDirection: 'row', gap: space.s3, marginTop: space.s4, justifyContent: 'center' }}>
          {[1, 2, 3, 4, 5].map((s) => (
            <Pressable
              key={s}
              accessibilityRole="button"
              accessibilityLabel={`${s} star${s === 1 ? '' : 's'}`}
              onPress={() => setStars(s)}
              hitSlop={6}
            >
              <AppText style={{ fontSize: 34, lineHeight: 44, color: s <= stars ? colors.accent700 : colors.borderFirm }}>★</AppText>
            </Pressable>
          ))}
        </View>
        <Field label={t('sid_rate_note_label')}>
          <Input value={rateNote} onChangeText={setRateNote} placeholder={t('sid_rate_note_ph')} />
        </Field>
        <Button
          title={t('sid_save_rating')}
          size="lg"
          loading={savingRating}
          disabled={!stars}
          onPress={() => void saveRating()}
          style={{ marginTop: space.s4 }}
        />
      </Sheet>

      {/* payment sheet */}
      <Sheet visible={payOpen} onClose={() => setPayOpen(false)}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>{t('sid_pay_title')}</AppText>
        <View style={{ gap: space.s4 }}>
          <Field label={t('sid_amount_label')}>
            <Input value={payAmount} onChangeText={setPayAmount} placeholder="4500" keyboardType="numeric" />
          </Field>
          <Field label={t('sid_note_label')}>
            <Input value={payNote} onChangeText={setPayNote} placeholder={t('sid_pay_note_ph')} />
          </Field>
        </View>
        <Button
          title={t('sid_record')}
          size="lg"
          loading={savingPay}
          disabled={!Number(payAmount.replace(/[^0-9.]/g, ''))}
          onPress={() => void savePayment()}
          style={{ marginTop: space.s4 }}
        />
        {payments.length ? (
          <View style={{ marginTop: space.s4, gap: space.s2 }}>
            <AppText variant="label" tone="tertiary">{t('sid_recent')}</AppText>
            {payments.slice(0, 6).map((pmt) => (
              <View key={pmt.id} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                <AppText variant="bodySm" tone="secondary" style={{ flex: 1 }} numberOfLines={1}>
                  {pmt.paidOn}{pmt.note ? ` · ${pmt.note}` : ''}
                </AppText>
                <AppText variant="bodySm" weight="600" mono>{formatINR(pmt.paise / 100)}</AppText>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Remove ${formatINR(pmt.paise / 100)} entry`}
                  onPress={() => void deletePayment(pmt.id)}
                  hitSlop={8}
                >
                  <AppText variant="bodySm" weight="700" tone="tertiary">✕</AppText>
                </Pressable>
              </View>
            ))}
          </View>
        ) : null}
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3 }}>
          {t('sid_pay_note_footer')}
        </AppText>
      </Sheet>
    </Screen>
  );
}
